// Phase-25.B — Shared client-side cache for the canonical category registry.
//
// Hits `GET /api/categories` once per session and serves the same snapshot to
// every consumer (Rule Builder multi-select, Recommend dropdown, Simulator).
//
// Why a hook + module-level memo:
//   • Avoids duplicate network round-trips across surfaces (Rule Builder may
//     be open alongside Recommend in admin previews).
//   • Lets the Rule Builder's "+ Add category" affordance push a freshly
//     created category into the cache so it appears immediately in the
//     picker dropdown without a page reload.
//
// Shape returned (array of Category):
//   [{ id, value, label, description, aliases[], icon, sort_order,
//      is_active, workspace, created_at, updated_at }]
//
// Surfaces consuming this hook MUST tolerate the cache returning [] briefly
// during the first paint — render a skeleton or "loading…" placeholder.

import { useEffect, useRef, useState } from 'react';
import { api } from '@/lib/api';

let _cache = null;          // last successful payload (active categories)
let _lastError = null;      // last error (cleared on next success)
let _inflight = null;       // dedupes concurrent fetches
const _subscribers = new Set();

const _notify = () => {
  for (const cb of _subscribers) {
    try { cb(); } catch (_) { /* swallow */ }
  }
};

async function _load() {
  if (_inflight) return _inflight;
  _inflight = api.get('/api/categories')
    .then((arr) => {
      _cache = Array.isArray(arr) ? arr : [];
      _lastError = null;
      _notify();
      return _cache;
    })
    .catch((err) => {
      // Module-level notify pattern: instead of letting consumers attach
      // their own .catch() inside React effects (which trips the strict
      // set-state-in-effect rule), we stash the error here and let the
      // subscriber's plain setState read it on the next paint.
      console.warn('useCategories: load failed', err);
      _lastError = err;
      _notify();
      throw err;
    })
    .finally(() => { _inflight = null; });
  return _inflight;
}

/** Force a refetch — call after creating/renaming/merging categories. */
export function invalidateCategories() {
  _cache = null;
  _load().catch(() => {});
}

/** Optimistic insert — push a freshly-created category into the cache so
 * the Rule Builder picker shows it immediately. The next `invalidateCategories`
 * call will reconcile with the server's authoritative copy. */
export function pushCategoryOptimistic(cat) {
  if (!cat || !cat.value) return;
  const next = Array.isArray(_cache) ? [..._cache] : [];
  if (!next.some((c) => c.value === cat.value)) {
    next.push(cat);
    next.sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.value.localeCompare(b.value));
    _cache = next;
    _notify();
  }
}

/** Read-only hook. Returns { categories, loading, error, refetch }. */
export function useCategories() {
  const [, forceRender] = useState(0);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;

    // The subscriber simply bumps a counter so React re-reads the module
    // state. setState NEVER originates from inside a Promise chain that
    // started in this effect — _load() runs in the module scope and the
    // subscriber is only invoked by _notify() on the next event loop tick
    // (Promise microtask). This satisfies react-hooks/set-state-in-effect.
    const subscriber = () => {
      if (!mounted.current) return;
      forceRender((n) => (n + 1) % 1_000_000);
    };
    _subscribers.add(subscriber);

    // Kick off the load if we have no snapshot yet. Errors are recorded
    // into _lastError and surfaced via the subscriber's re-render — we
    // don't .catch() here, so React's strict effect rule has nothing to
    // complain about.
    if (!_cache && !_inflight) {
      _load().catch(() => { /* errors handled inside _load */ });
    }

    return () => {
      mounted.current = false;
      _subscribers.delete(subscriber);
    };
  }, []);

  return {
    categories: _cache || [],
    loading: !_cache && !_lastError,
    error: _lastError,
    refetch: () => {
      _cache = null;
      _lastError = null;
      return _load().catch(() => null);
    },
  };
}
